import { ReactNode } from 'react';
import { Inbox, LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { CreateButton } from './CreateButton';
import { EMPTY_STATE_TITLE } from '../../styles/typography';

interface EmptyStateProps {
  title?: string;
  message?: string;
  /** Lucide icon for the illustration. Pass `null` to render no icon (dense inline empties). */
  icon?: LucideIcon | null;
  /** Action button - pass the entity name only (e.g., "Organization"); a Plus icon is added */
  action?: ReactNode | {
    label: string;
    onClick: () => void;
  };
  children?: ReactNode;
  className?: string;
  /**
   * Dense variant for inline empties that live INSIDE a table, tab, or small
   * card section (e.g. a `<TableCell colSpan>` "no rows" row, or a chart card
   * with no data) — smaller icon, tighter padding, and a small sans title
   * instead of the display serif. The default (full) variant stays the
   * page-level "create your first X" empty. In compact mode the title is
   * omitted entirely when not provided (message-only empties are common inline).
   */
  compact?: boolean;
}

export function EmptyState({
  title,
  message,
  icon: Icon = Inbox,
  action,
  children,
  className,
  compact = false,
}: EmptyStateProps) {
  // Full-variant dev fallbacks, kept out of JSX so the i18next lint rule doesn't
  // treat them as user-facing literals (they're only shown when a caller passes
  // neither title nor message).
  const fullTitle = title ?? 'No data';
  const fullMessage = message ?? 'There are no items to display.';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-6' : 'px-4 py-8 sm:py-12',
        className
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'text-muted-foreground/50',
            compact
              ? 'mb-2 h-8 w-8'
              : 'mb-3 h-12 w-12 sm:mb-4 sm:h-16 sm:w-16'
          )}
        />
      )}
      {/* Full variant renders title + message unconditionally (with historical
          English fallbacks + fixed spacing) so the ~40 existing callers are
          untouched. Compact renders only what's supplied — inline empties are
          typically message-only — with a smaller sans title. */}
      {compact ? (
        title && (
          <h3 className="mb-1 text-sm font-medium text-muted-foreground">{title}</h3>
        )
      ) : (
        <h3 className={cn('mb-1 text-muted-foreground', EMPTY_STATE_TITLE)}>
          {fullTitle}
        </h3>
      )}
      {compact ? (
        message && (
          <p className={cn('max-w-sm text-sm text-muted-foreground', action && 'mb-3')}>
            {message}
          </p>
        )
      ) : (
        <p className="mb-4 max-w-sm text-sm text-muted-foreground sm:mb-6">
          {fullMessage}
        </p>
      )}
      {action && (
        typeof action === 'object' && 'onClick' in action ? (
          <CreateButton onClick={action.onClick} className="w-full max-w-xs sm:w-auto">
            {action.label}
          </CreateButton>
        ) : (
          <div className="w-full max-w-xs sm:w-auto">
            {action}
          </div>
        )
      )}
      {children}
    </div>
  );
}
